import React, { useState, useRef } from 'react';
import { Upload, X, File, Trash2, Download, Eye, AlertCircle, Image, Video, Music, FileText, Code } from 'lucide-react';

// Custom scrollbar styles for FileUpload
const fileUploadScrollbarStyles = `
  /* WebKit browsers (Chrome, Safari, Edge) */
  .file-upload-scrollbar::-webkit-scrollbar {
    width: 6px;
  }

  .file-upload-scrollbar::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3px;
  }

  .file-upload-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.12);
    border-radius: 3px;
    transition: all 0.2s ease;
  }

  .file-upload-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.2);
  }

  /* Dark mode */
  .dark .file-upload-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.12);
  }

  .dark .file-upload-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  /* Firefox */
  .file-upload-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.12) transparent;
  }

  .dark .file-upload-scrollbar {
    scrollbar-color: rgba(255, 255, 255, 0.12) transparent;
  }

  /* Smooth scrolling */
  .file-upload-scrollbar {
    scroll-behavior: smooth;
  }

  /* Hide scrollbar when not needed */
  .file-upload-scrollbar:not(:hover)::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.08);
  }

  .dark .file-upload-scrollbar:not(:hover)::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.08);
  }

  /* Ultra-compact scrollbar for file lists */
  .file-list-scrollbar::-webkit-scrollbar {
    width: 4px;
  }

  .file-list-scrollbar::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 2px;
    margin: 2px 0;
  }

  .file-list-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 2px;
    transition: all 0.2s ease;
  }

  .file-list-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.18);
  }

  .dark .file-list-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.1);
  }

  .dark .file-list-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.18);
  }

  .file-list-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.1) transparent;
  }

  .dark .file-list-scrollbar {
    scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
  }
`;

// Inject styles into the document head
const injectFileUploadScrollbarStyles = () => {
  if (typeof document !== 'undefined') {
    const existingStyle = document.getElementById('file-upload-scrollbar-styles');
    if (!existingStyle) {
      const style = document.createElement('style');
      style.id = 'file-upload-scrollbar-styles';
      style.textContent = fileUploadScrollbarStyles;
      document.head.appendChild(style);
    }
  }
};

const FileUpload = ({ onFileUpload, onMultipleFileUpload, files, onFileDelete, onFileDownload, loading, error, onClearError, readOnly = false }) => {
  // Inject scrollbar styles on component mount
  React.useEffect(() => {
    injectFileUploadScrollbarStyles();
  }, []);

  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = async (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files.length > 0) {
      await handleFiles(e.target.files);
    }
  };

  const handleFiles = async (fileList) => {
    if (onClearError) onClearError();
    
    if (fileList.length === 1) {
      // Single file upload
      try {
        setUploadProgress('Uploading...');
        await onFileUpload(fileList[0]);
        setUploadProgress(null);
      } catch (error) {
        setUploadProgress(null);
        console.error('Upload failed:', error);
      }
    } else {
      // Multiple file upload
      try {
        setUploadProgress(`Uploading ${fileList.length} files...`);
        await onMultipleFileUpload(fileList);
        setUploadProgress(null);
      } catch (error) {
        setUploadProgress(null);
        console.error('Upload failed:', error);
      }
    }
  };

  const handleDownload = async (file) => {
    try {
      await onFileDownload(file.id, file.original_filename);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (contentType) => {
    const iconProps = { size: 14, className: "text-ui-text-tertiary" };
    
    if (contentType?.startsWith('image/')) return <Image {...iconProps} className="text-pink-500 dark:text-pink-400" />;
    if (contentType?.startsWith('video/')) return <Video {...iconProps} className="text-purple-500 dark:text-purple-400" />;
    if (contentType?.startsWith('audio/')) return <Music {...iconProps} className="text-green-500 dark:text-green-400" />;
    if (contentType?.includes('pdf')) return <FileText {...iconProps} className="text-red-500 dark:text-red-400" />;
    if (contentType?.includes('text/')) return <FileText {...iconProps} className="text-blue-500 dark:text-blue-400" />;
    if (contentType?.includes('json') || contentType?.includes('javascript')) return <Code {...iconProps} className="text-yellow-500 dark:text-yellow-400" />;
    return <File {...iconProps} />;
  };

  return (
    <div className={`h-full flex flex-col space-y-4`}>
      {/* Error Display */}
      {error && (
        <div className="flex items-center p-3 bg-semantic-error/10 border border-semantic-error/20 rounded-xl">
          <AlertCircle size={16} className="mr-2 text-semantic-error" />
          <span className="text-sm text-semantic-error">{error}</span>
          {onClearError && (
            <button
              onClick={onClearError}
              className="ml-auto text-semantic-error hover:text-semantic-error/80 p-1 rounded-lg hover:bg-semantic-error/10 transition-colors"
            >
              <X size={14} />
            </button>
          )}
        </div>
      )}

      {/* Compact Upload Area */}
      <div
        className={`border-2 border-dashed rounded-xl p-4 text-center transition-all duration-200 ${
          dragActive 
            ? 'border-brand-primary bg-brand-primary/10 scale-[1.02]' 
            : 'border-slate-300 dark:border-neutral-600 hover:border-brand-primary hover:bg-brand-primary/5'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleChange}
          disabled={readOnly}
        />
        
        <div className="flex items-center justify-center space-x-3">
          <Upload 
            size={20} 
            className={`${
              dragActive ? 'text-brand-primary' : 'text-slate-500 dark:text-neutral-500'
            } transition-colors`} 
          />
          
          {uploadProgress ? (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-2 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm font-medium text-brand-primary">
                {uploadProgress}
              </span>
            </div>
          ) : (
            <div className="text-left">
              <p className="text-sm font-medium text-neutral-900 dark:text-white">
                Drop files here or{' '}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-brand-primary hover:text-brand-primary/80 hover:underline transition-colors"
                  disabled={loading || readOnly}
                >
                  browse
                </button>
              </p>
              <p className="text-xs text-ui-text-tertiary mt-0.5">
                Any file type, up to 50MB per file
              </p>
            </div>
          )}
        </div>
      </div>

      {/* File List */}
      {files && files.length > 0 && (
        <div className={`flex-1 min-h-0 space-y-3`}>
          <div className={`h-full overflow-y-auto space-y-2 file-list-scrollbar`}>
            {files.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between p-3 bg-ui-background-secondary rounded-xl hover:bg-ui-background-tertiary transition-all duration-200 group"
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 p-1.5 rounded-lg bg-white dark:bg-neutral-800 shadow-sm">
                    {getFileIcon(file.content_type)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate text-ui-text-primary" title={file.original_filename}>
                      {file.original_filename}
                    </p>
                    <div className="flex items-center space-x-2 mt-0.5">
                      <span className="text-xs text-ui-text-tertiary">
                        {formatFileSize(file.file_size)}
                      </span>
                      <span className="text-xs text-ui-text-tertiary">•</span>
                      <span className="text-xs text-ui-text-tertiary">
                        {formatDate(file.created_at)}
                      </span>
                    </div>
                    {file.content_type && (
                      <p className="text-xs text-ui-text-tertiary opacity-75 mt-0.5">
                        {file.content_type}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-1 ml-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleDownload(file)}
                    className="p-2 text-ui-text-tertiary hover:text-semantic-info hover:bg-semantic-info/10 transition-all rounded-lg"
                    title="Download file"
                  >
                    <Download size={14} />
                  </button>
                  
                  <button
                    onClick={() => !readOnly && onFileDelete(file.id)}
                    className="p-2 text-ui-text-tertiary hover:text-semantic-error hover:bg-semantic-error/10 transition-all rounded-lg"
                    title="Delete file"
                    disabled={readOnly}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {files && files.length === 0 && !loading && (
        <div className="text-center py-6 border border-dashed border-slate-300 dark:border-neutral-600 rounded-xl">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-slate-200 dark:bg-neutral-700 flex items-center justify-center">
            <File size={20} className="text-slate-500 dark:text-neutral-500" />
          </div>
          <p className="text-sm text-ui-text-primary font-medium mb-1">No files uploaded yet</p>
          <p className="text-xs text-ui-text-tertiary">Upload files to get started</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-6">
          <div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full mx-auto mb-3 animate-spin"></div>
          <p className="text-sm text-ui-text-primary font-medium mb-1">Loading files...</p>
          <p className="text-xs text-ui-text-tertiary">Please wait while we fetch your files</p>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
